import type { ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '../../hooks/useMsal';
import { ROUTES } from '../../utils/constants';

interface RoleGuardProps {
  children: ReactNode;
  roles: string[];
  requireAll?: boolean;
}

/**
 * Role-based access control component
 * Checks if user has required role(s) before rendering children
 */
export const RoleGuard = ({ children, roles, requireAll = false }: RoleGuardProps) => {
  const { hasAnyRole, hasAllRoles } = useMsal();
  const navigate = useNavigate();

  const hasAccess = requireAll ? hasAllRoles(roles) : hasAnyRole(roles);

  if (!hasAccess) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 3,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 500,
            textAlign: 'center',
          }}
        >
          <LockIcon
            sx={{
              fontSize: 64,
              color: 'error.main',
              mb: 2,
            }}
          />
          <Typography variant="h5" gutterBottom>
            Přístup odepřen
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Nemáte dostatečná oprávnění pro přístup k této stránce.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Požadované role: {roles.join(', ')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate(ROUTES.DASHBOARD)}
            sx={{ mt: 2 }}
          >
            Zpět na hlavní stránku
          </Button>
        </Paper>
      </Box>
    );
  }

  return <>{children}</>;
};
